/**
 * Arch Hangar Orchestrator
 *
 * Coordinates rendering of arch/Quonset-style hangar buildings.
 * Applies building transformation (position, rotation) to the rendered unit.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import type { ArchHangarParams, ArchProfile } from '@/lib/templates/arch-hangar'
import { DEFAULT_ARCH_HANGAR_VALUES } from '@/lib/templates/arch-hangar'
import { renderArchUnit } from '../composers/ArchUnitComposer'

/**
 * Building render options for arch hangar
 */
export interface ArchHangarRenderOptions {
  buildingId?: string
  buildingName?: string
  position?: [number, number, number]
  rotationDeg?: number
  wallColor?: string
  floorColor?: string
  wallThickness?: number
  enableShadows?: boolean
}

/**
 * Reconstruct ArchHangarParams from building type_config and settings.
 * Used when loading saved buildings from the database.
 */
function reconstructArchParams(
  building: GeneratedBuilding
): ArchHangarParams {
  const tc = building.type_config as Record<string, unknown>

  return {
    name: building.name,
    code: building.code,
    latitude: building.latitude,
    longitude: building.longitude,
    bearing_deg: (building.rotation * 180) / Math.PI,
    floor_color: building.settings?.floor_color || DEFAULT_ARCH_HANGAR_VALUES.floor_color,
    wall_color: building.settings?.wall_color || DEFAULT_ARCH_HANGAR_VALUES.wall_color,
    span_ft: (tc.span_ft as number) ?? building.exterior_width,
    length_ft: (tc.length_ft as number) ?? building.exterior_depth,
    crown_height_ft: (tc.crown_height_ft as number) ?? building.exterior_width / 2,
    knee_wall_height_ft: (tc.knee_wall_height_ft as number) ?? 0,
    arch_profile: (tc.arch_profile as ArchProfile) ?? 'semicircular',
    arch_segments: (tc.arch_segments as number) ?? 24,
    wall_thickness_ft: (tc.wall_thickness_ft as number) ?? 0.5,
    front_door: (tc.front_door as ArchHangarParams['front_door']) ?? DEFAULT_ARCH_HANGAR_VALUES.front_door,
    back_door: (tc.back_door as ArchHangarParams['back_door']) ?? DEFAULT_ARCH_HANGAR_VALUES.back_door,
  }
}

/**
 * Render an arch hangar building
 */
export function renderArchHangarBuilding(
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: ArchHangarRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    enableShadows = true,
  } = options

  if (units.length !== 1) {
    console.warn(`ArchHangarOrchestrator: Expected 1 unit, got ${units.length}`)
  }

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'arch_hangar',
  }

  // Reconstruct arch params from building config
  const archParams = reconstructArchParams(building)

  // Get colors from building settings
  const floorColor = building.settings?.floor_color || options.floorColor
  const wallColor = building.settings?.wall_color || options.wallColor

  // Render the single arch unit
  const unit = units[0]
  if (unit) {
    const unitGroup = renderArchUnit(unit, {
      floorColor,
      wallColor,
      enableShadows,
      archParams,
    })

    // The arch unit is centered at origin (arch profile is symmetric around x=0,
    // and the shell is symmetric around z=0), so no centering offset needed
    group.add(unitGroup)
  }

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180) / Math.PI
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180
  }

  // Apply building position (convert feet to meters)
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}
